import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import 'dayjs/locale/id';
import 'dayjs/locale/zh-cn';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import type { StatementConfigDTO } from '@/app/(dashboard)/master/statement/apis/use-get-statement-configs';

interface StatementConflictPanelProps {
  /** The colleague's row; null while it is being refetched. */
  current: StatementConfigDTO | null;
  onReload: () => void;
  onOverwrite: () => void;
  overwriting?: boolean;
  /** The colleague's row could not be read: offer a retry instead of dead buttons. */
  loadFailed?: boolean;
  onRetryLoad?: () => void;
}

const DAYJS_LOCALE: Record<string, string> = { id: 'id', zh: 'zh-cn', en: 'en' };

/**
 * Shown in a Pernyataan form when its save hit a 409: what the colleague saved,
 * with "Muat ulang" (take their values) and "Timpa" (resend ours on top).
 */
export function StatementConflictPanel({
  current,
  onReload,
  onOverwrite,
  overwriting,
  loadFailed,
  onRetryLoad,
}: StatementConflictPanelProps) {
  const { colors } = useTheme();
  const { t, language } = useLanguage();
  const when = current?.updatedAt ?? current?.createdAt;
  const formatWhen = (value: string) =>
    dayjs(value)
      .locale(DAYJS_LOCALE[language] ?? 'id')
      .format('DD MMM YYYY HH:mm');
  const byLine =
    current?.updatedByEmail && when
      ? t('statement.konflikDiperbaruiOleh')
          .replace('{email}', current.updatedByEmail)
          .replace('{date}', formatWhen(when))
      : when
        ? t('statement.konflikDiperbaruiPada').replace('{date}', formatWhen(when))
        : null;
  const rows: [string, string | undefined][] = [
    [t('statement.kolomTempat'), current?.place],
    [t('statement.kolomNama'), current?.name],
    [t('statement.kolomJabatan'), current?.position],
  ];

  return (
    <Alert
      data-statement-conflict=""
      role="alert"
      variant="light"
      color="orange"
      icon={<IconAlertTriangle size={18} />}
      title={t('statement.konflikJudul')}
      styles={{ title: { color: colors.text, fontWeight: 700 }, message: { color: colors.text } }}
    >
      <Stack gap={8}>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 2 }}>
          {rows.map(([label, value]) => (
            <div key={label} style={{ display: 'contents' }}>
              <Text size="sm" style={{ color: colors.textSecondary }}>
                {label}
              </Text>
              <Text size="sm" fw={600} style={{ color: colors.text, wordBreak: 'break-word' }}>
                {value || '-'}
              </Text>
            </div>
          ))}
        </div>
        {byLine ? (
          <Text size="xs" style={{ color: colors.textSecondary }}>
            {byLine}
          </Text>
        ) : null}
        {!current && loadFailed ? (
          <Group gap="xs" mt={2}>
            <Text size="xs" style={{ color: colors.textSecondary }}>
              {t('statement.konflikGagalMemuat')}
            </Text>
            <Button size="xs" variant="default" onClick={onRetryLoad}>
              {t('statement.konflikMuatUlangDaftar')}
            </Button>
          </Group>
        ) : (
          <Group gap="xs" mt={2}>
            <Button size="xs" variant="default" onClick={onReload} disabled={overwriting || !current}>
              {t('statement.konflikMuatUlang')}
            </Button>
            <Button size="xs" color="orange" onClick={onOverwrite} loading={overwriting} disabled={!current}>
              {t('statement.konflikTimpa')}
            </Button>
          </Group>
        )}
      </Stack>
    </Alert>
  );
}
